import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { createPhase, updatePhase, EventPhase } from '../api';
import { useToast } from '../../../context/ToastContext';

const labelCls = 'mb-1 block text-xs font-medium text-content-2';
const inputCls = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-brand-blue';

/** datetime "YYYY-MM-DDTHH:mm:ss" → "YYYY-MM-DDTHH:mm" para el input datetime-local. */
function toInput(dt: string | null | undefined) {
  if (!dt) return '';
  const m = dt.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}

/** Valores prellenados (p. ej. desde «Adicionales sugeridos»: máximo propuesto y banderas de la fase 2). Mandan sobre `phase`. */
export interface PhasePreset { name?: string; max_per_account?: number | null; solo_participantes?: boolean; solo_lista_espera?: boolean; sort?: number; price?: number | null }

export default function PhaseForm({ eventId, phase, preset, festival = false, onClose, onSaved }: {
  eventId: number; phase?: EventPhase | null; preset?: PhasePreset; festival?: boolean; onClose: () => void; onSaved: () => void;
}) {
  const toast = useToast();
  const editing = !!phase;
  const [name, setName] = useState(preset?.name ?? phase?.name ?? '');
  const [startsAt, setStartsAt] = useState(toInput(phase?.starts_at));
  const [endsAt, setEndsAt] = useState(toInput(phase?.ends_at));
  const [capacity, setCapacity] = useState(phase?.capacity != null ? String(phase.capacity) : '');
  const maxIni = preset?.max_per_account !== undefined ? preset.max_per_account : phase?.max_per_account;
  const [maxPer, setMaxPer] = useState(maxIni != null ? String(maxIni) : '');
  const precioIni = preset?.price !== undefined ? preset.price : phase?.price;
  const [price, setPrice] = useState(precioIni != null ? String(precioIni) : '');
  const [active, setActive] = useState(phase ? phase.active === 1 : true);
  // Modo festival (Mabel 28-oct-2026): F1 «solo participantes» · F2 «solo quien dejó lista de espera».
  const [soloPart, setSoloPart] = useState(preset?.solo_participantes ?? (phase ? Number(phase.solo_participantes || 0) === 1 : false));
  const [soloEspera, setSoloEspera] = useState(preset?.solo_lista_espera ?? (phase ? Number(phase.solo_lista_espera || 0) === 1 : false));
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Ponle un nombre a la fase'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        starts_at: startsAt || null,
        ends_at: endsAt || null,
        capacity: capacity === '' ? null : Number(capacity),
        max_per_account: maxPer === '' ? null : Number(maxPer),
        price: price === '' ? null : Number(price),
        sort: preset?.sort ?? phase?.sort ?? 0,
        active: active ? 1 : 0,
        solo_participantes: soloPart ? 1 : 0,
        solo_lista_espera: soloEspera ? 1 : 0,
      };
      if (editing) { await updatePhase(eventId, phase!.phase_id, payload); toast.success('Fase actualizada'); }
      else { await createPhase(eventId, payload); toast.success('Fase creada'); }
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || 'No se pudo guardar la fase');
    } finally { setSaving(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{editing ? 'Editar fase' : 'Nueva fase de venta'}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Nombre de la fase *</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Fase 1 / Preventa" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Inicio (opcional)</label>
              <input type="datetime-local" className={inputCls} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Fin (opcional)</label>
              <input type="datetime-local" className={inputCls} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Cupo (boletos)</label>
              <input type="number" min="0" step="1" className={inputCls} value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="—" />
            </div>
            <div>
              <label className={labelCls}>Máx/alumno</label>
              <input type="number" min="1" step="1" className={inputCls} value={maxPer} onChange={(e) => setMaxPer(e.target.value)} placeholder="—" />
            </div>
            <div>
              <label className={labelCls}>Precio</label>
              <input type="number" min="0" step="1" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="evento" />
            </div>
          </div>
          <p className="text-xs text-content-3">
            {festival
              ? 'Cupo y máx/alumno vacíos = sin tope. Precio vacío = usa el precio del evento. Lo que un participante pida por encima del máximo queda en lista de espera.'
              : 'Cupo y máx/alumno vacíos = sin tope. Precio vacío = usa el precio del evento. Al llenarse el cupo, los nuevos boletos pasan a lista de espera.'}
          </p>
          <label className="flex items-center gap-2 text-sm text-content-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="h-4 w-4 rounded border-line" />
            Fase activa (disponible para vender)
          </label>
          {festival && (
            <div className="space-y-2 rounded-xl border border-line bg-surface-2/60 p-3">
              <label className="flex items-start gap-2 text-sm text-content-2">
                <input type="checkbox" name="solo_participantes" checked={soloPart} onChange={(e) => setSoloPart(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line" />
                <span><strong className="text-content">Solo participantes del festival</strong>
                  <span className="block text-xs text-content-3">Solo pueden comprar los alumnos de la lista de participantes (compra prioritaria). Sin palomita, cualquiera compra (venta general y externos).</span></span>
              </label>
              <label className="flex items-start gap-2 text-sm text-content-2">
                <input type="checkbox" name="solo_lista_espera" checked={soloEspera} onChange={(e) => setSoloEspera(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-line" />
                <span><strong className="text-content">Solo quien dejó lista de espera</strong>
                  <span className="block text-xs text-content-3">Fase de adicionales: solo compra quien pidió de más en la fase anterior, y lo que se le vende descuenta su lista de espera.</span></span>
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-content-2 hover:bg-surface-2">Cancelar</button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60">
              {saving ? 'Guardando…' : editing ? 'Guardar' : 'Crear fase'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
